import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import {
  UsersIconFilled,
  MinusIcon,
  PhoneIcon,
  PlusIcon,
} from '@rsos/assets/icons';
import ChatAvatar from '@rsos/components/capstone/Chat/ChatAvatar';
import { CENTRAL_STATION } from '@rsos/constants/appNames.js';
import Popover from '../../base/Popover';
import ChatParticipants from '../ChatParticipants';
import {
  Container,
  AvatarBox,
  AgencyInfo,
  AgencyName,
  AgencyPhone,
  PhoneNumber,
  AgenciesTitle,
  AgenciesList,
  ActionsBox,
  ActionButton,
  UnreadPill,
  LineClamp,
  chatHeaderIconColor,
} from './ChatHeader.styles';

/**
 * Displays a chat header above the chat box. For Alerts, there is only a single chat participant
 * between Central Station and RapidSOS Portal; However, for 911 calls, there can be multiple chat
 * participants between agencies in RapidSOS Portal.
 *
 * @param {object} alertChatParticipant - chat participant for alerts
 * @param {array} callChatParticipants - list of chat participants for 911 calls
 * @param {boolean} isChatBoxVisible - is the chatbox visible or not
 * @param {boolean} isUnread - are there unread messages in the chat or not
 * @param {Function} onActionButtonClick - callback function to collapse/expand the chat
 */
const ChatHeader = ({
  alertChatParticipant,
  callChatParticipants,
  isChatBoxVisible,
  isUnread,
  onActionButtonClick,
}) => {
  const isCentralStation = useSelector(
    state => state.sinatra.user.currentRole.application === CENTRAL_STATION,
  );
  const [isParticipantsPopoverOpen, setIsParticipantsPopoverOpen] = useState(
    false,
  );

  const selectedAlertID = useSelector(state => state.incidents.selectedAlertID);
  const selectedCallerID = useSelector(
    state => state?.emergencyCalls?.selectedCallerID,
  );
  const fetchAlertChatParticipantName = alertChatParticipant => {
    return alertChatParticipant && alertChatParticipant.name
      ? alertChatParticipant.name
      : 'ECC Unavailable';
  };
  return (
    <Container isUnread={isUnread} isChatBoxVisible={isChatBoxVisible}>
      {(alertChatParticipant || isCentralStation) && (
        <>
          <AvatarBox>
            <ChatAvatar
              name={fetchAlertChatParticipantName(alertChatParticipant)}
            />
          </AvatarBox>
          <AgencyInfo>
            <AgencyName>
              {fetchAlertChatParticipantName(alertChatParticipant)}
            </AgencyName>
            {alertChatParticipant && alertChatParticipant.phone && (
              <AgencyPhone>
                <PhoneIcon
                  width={10}
                  height={12}
                  viewboxWidth={11}
                  viewboxHeight={15}
                  color={chatHeaderIconColor}
                />
                <PhoneNumber>{alertChatParticipant.phone}</PhoneNumber>
              </AgencyPhone>
            )}
          </AgencyInfo>
        </>
      )}
      {callChatParticipants && callChatParticipants.length > 0 && (
        <>
          <AvatarBox>
            <ChatAvatar
              name={callChatParticipants[0].display_name}
              isOverlapped={callChatParticipants.length > 1}
            />
          </AvatarBox>
          <AgencyInfo>
            <LineClamp>
              <AgenciesTitle>
                {callChatParticipants.length > 1
                  ? `${callChatParticipants.length} Agencies`
                  : '1 Agency'}
              </AgenciesTitle>{' '}
              {callChatParticipants.map((participant, index) => [
                index > 0 && ', ',
                <AgenciesList key={index}>
                  {participant.display_name}
                </AgenciesList>,
              ])}
            </LineClamp>
          </AgencyInfo>
        </>
      )}

      <ActionsBox>
        {isUnread && !isChatBoxVisible ? (
          <UnreadPill>New</UnreadPill>
        ) : (
          <ActionButton
            onMouseEnter={() => setIsParticipantsPopoverOpen(true)}
            onMouseLeave={() => setIsParticipantsPopoverOpen(false)}
          >
            <Popover
              cssDisplayProperty="block"
              isOpen={isParticipantsPopoverOpen}
              popoverID={`chat-popover`}
              trigger={
                <UsersIconFilled
                  width={12}
                  height={12}
                  color={chatHeaderIconColor}
                />
              }
              useCustomOffset
              xOffset={-6}
              yOffset={20}
            >
              <ChatParticipants
                alertChatParticipants={
                  selectedAlertID && alertChatParticipant
                    ? [alertChatParticipant]
                    : null
                }
                callChatParticipants={
                  selectedCallerID ? callChatParticipants : []
                }
              />
            </Popover>
          </ActionButton>
        )}
        <ActionButton noMargin showBorder onClick={onActionButtonClick}>
          {!isChatBoxVisible ? (
            <PlusIcon width={14} height={14} color={chatHeaderIconColor} />
          ) : (
            <MinusIcon width={12} height={12} color={chatHeaderIconColor} />
          )}
        </ActionButton>
      </ActionsBox>
    </Container>
  );
};

ChatHeader.propTypes = {
  alertChatParticipant: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    phone: PropTypes.string,
  }),
  isChatBoxVisible: PropTypes.bool,
  isUnread: PropTypes.bool,
  onActionButtonClick: PropTypes.func,
};

ChatHeader.defaultProps = {
  onActionButtonClick: () => {},
};
export default ChatHeader;
